import { useState, useCallback } from 'react';
import { MATCH_RALLY_API } from '../config/endpoints';
import { deriveVisualizationPaths } from '../utils/rallyDataUtils';

/**
 * Custom hook for fetching rally data from backend API
 * Handles loading states, errors, and data transformation
 * 
 * @returns {Object} Hook interface
 * @returns {Function} fetchRallyData - Function to fetch rally data for a video
 * @returns {boolean} loading - Loading state
 * @returns {string|null} error - Error message if any
 * @returns {Object|null} rallyData - Fetched and processed rally data
 */
export const useRallyData = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rallyData, setRallyData] = useState(null);

    /**
     * Fetches rally data from the backend API
     * Backend uses match_id to look up rally data
     * @param {string} matchId - Match ID (e.g., "IMG_0539")
     * @returns {Promise<Object>} Processed rally data with visualization paths
     */
    const fetchRallyData = useCallback(async (matchId, postId) => {
        if (!matchId) {
            const errorMsg = 'Match ID is required';
            setError(errorMsg);
            throw new Error(errorMsg);
        }

        // Get JWT token from localStorage
        const token = localStorage.getItem('BQS_TOKEN');
        if (!token) {
            const errorMsg = 'Authentication required. Please log in.';
            setError(errorMsg);
            throw new Error(errorMsg);
        }

        setLoading(true);
        setError(null);

        try {
            console.log(`[useRallyData] Fetching rally data for match ID: ${matchId}, postId: ${postId}`);

            let url = `${MATCH_RALLY_API}/${encodeURIComponent(matchId)}`;
            if (postId) {
                url += `?postId=${encodeURIComponent(postId)}`;
            }
            console.log(`[useRallyData] API URL: ${url}`);

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                // Enhanced error message for 401
                if (response.status === 401) {
                    const errorData = await response.json().catch(() => ({}));
                    console.error('[useRallyData] 401 Unauthorized - Backend needs to link rally data to postId for authorization');
                    throw new Error(errorData.message || 'You do not have access to this match rally data');
                }
                // 404 - Rally data not found
                if (response.status === 404) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.message || 'Match rally data not found for this video');
                }
                throw new Error(`Failed to fetch rally data: ${response.status} ${response.statusText}`);
            }

            const responseData = await response.json();
            console.log('[useRallyData] Received response:', responseData);

            // Extract data from response wrapper
            const data = responseData.data || responseData;
            console.log('[useRallyData] Rally data:', data);

            // Derive visualization paths from the response
            const paths = deriveVisualizationPaths(data);
            console.log('[useRallyData] Derived paths:', paths);

            const processedData = { ...data, ...paths };
            setRallyData(processedData);

            return processedData;
        } catch (err) {
            console.error('[useRallyData] Error fetching rally data:', err);
            const errorMsg = err.message || 'Failed to fetch rally data';
            setError(errorMsg);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Clears error state
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    /**
     * Resets all state
     */
    const reset = useCallback(() => {
        setLoading(false);
        setError(null);
        setRallyData(null);
    }, []);

    return {
        fetchRallyData,
        loading,
        error,
        rallyData,
        clearError,
        reset
    };
};
